'use client'

import { useScreenMatch } from 'lib/utilities/screens'
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import BookingWidgetMobile from './BookingWidgetMobile'
import BookingWidgetContent, {
  BookingWidgetContentProps,
} from './partials/BookingWidgetContent'
import {
  BookingWidgetContextData,
  BookingWidgetProvider,
  BookingWidgetProviderProps,
} from './providers/BookingWidgetContext'

import { AutoCompleteItem } from 'lib/types/AutoCompleteInput'
import { AddressInputAddress } from '../Form/AddressInput/AddressInput'

export type BookingWidgetData = Pick<
  BookingWidgetContextData,
  'tripType' | 'isCruise' | 'products' | 'services' | 'locations'
>

export interface BookingWidgetStore {
  data: Record<string, any>
  set: (data: BookingWidgetStore['data']) => void
}

export interface BookingWidgetProps
  extends Pick<
    BookingWidgetProviderProps,
    'brand' | 'loadingServiceLevels' | 'partner' | 'productOptions'
  > {
  /**
   * Optional array of address objects.
   */
  addresses?: (AddressInputAddress | null)[][]
  /**
   * Optional array of autocomplete loading values.
   */
  areAutoCompleteItemsLoading?: boolean[][]
  /**
   * Optional array of autocomplete items.
   */
  autoCompleteItems?: AutoCompleteItem[][][]
  /**
   * Optional function to compute the total price.
   */
  computeTotalPrice?: BookingWidgetProviderProps['computeTotal']
  /**
   * Optional function to get service options.
   */
  serviceOptions?: BookingWidgetContextData['serviceOptions']
  /**
   * Optional flag to disable sticky behavior.
   */
  noSticky?: boolean
  /**
   * Optional callback to handle data changes.
   */
  onChange?: (data?: BookingWidgetData) => void
  /**
   * Optional array of address input handlers.
   */
  onInputSearchTermHandlers?: ((text: string) => void)[][]
  /**
   * Optional array of address select handlers.
   */
  onSelectAutoCompleteItemHandlers?: ((item: AutoCompleteItem) => void)[][]
  /**
   * Optional callback to handle form submission.
   */
  onSubmit?: (data?: BookingWidgetData) => void
  /**
   * Partner's field on initiation
   */
  partnerField?: 'origin' | 'destination'
  /**
   * Total price of the booking.
   */
  totalPriceFormatted?: string
}

const bookingWidgetStickyContainerID = 'booking-widget-sticky-container'

export const BookingWidgetStickyContainer = () => {
  const isDesktop = useScreenMatch('lg')
  const stickyRef = useRef<HTMLDivElement | undefined>(undefined)

  useEffect(() => {
    if (stickyRef?.current) {
      if (isDesktop) {
        const observer = new MutationObserver(([{ addedNodes }]) => {
          const widget = addedNodes[0]
          if (widget) {
            requestAnimationFrame(() =>
              (widget as HTMLElement).classList.add('!translate-y-0'),
            )
          }
        })

        observer.observe(stickyRef.current, { childList: true })

        return () => observer.disconnect()
      } else {
        if (stickyRef?.current.firstElementChild) {
          stickyRef.current.removeChild(stickyRef.current.firstElementChild)
        }
      }
    }
  }, [stickyRef, isDesktop])

  return (
    <div
      className="relative w-full h-0 overflow-visible [&>*]:-translate-y-full"
      id={bookingWidgetStickyContainerID}
      ref={stickyRef as MutableRefObject<HTMLDivElement>}
    ></div>
  )
}

export default function BookingWidget({
  addresses = [],
  areAutoCompleteItemsLoading = [],
  autoCompleteItems = [],
  brand,
  computeTotalPrice,
  loadingServiceLevels = false,
  serviceOptions,
  noSticky,
  onChange,
  onInputSearchTermHandlers = [],
  onSelectAutoCompleteItemHandlers = [],
  onSubmit,
  partner,
  partnerField = 'destination',
  productOptions = [],
  totalPriceFormatted,
}: BookingWidgetProps) {
  const isDesktop = useScreenMatch('lg')
  const desktopContainerRef = useRef<HTMLDivElement | undefined>(undefined)
  const desktopWidgetRef = useRef<HTMLFormElement | undefined>(undefined)
  const [isSticky, setSticky] = useState<boolean>(false)

  const setStickyState = useCallback((isSticky: boolean) => {
    setSticky(isSticky)
  }, [])

  const handleSubmit = useCallback<
    Required<BookingWidgetContentProps>['onSubmit']
  >(
    (data) => {
      onSubmit?.(data)
    },
    [onSubmit],
  )

  useEffect(() => {
    const stickyWrapper = document.getElementById(
      bookingWidgetStickyContainerID,
    )

    if (
      !noSticky &&
      desktopContainerRef.current &&
      desktopWidgetRef.current &&
      stickyWrapper &&
      isDesktop
    ) {
      const intersectionObserver = new IntersectionObserver(
        ([{ isIntersecting, target }]) => {
          if (isIntersecting && !target.childElementCount) {
            // If visible in viewport, move the widget to the desktop container
            target.insertAdjacentElement(
              'afterbegin',
              desktopWidgetRef.current as HTMLFormElement,
            )
            target.firstElementChild?.classList.remove('!translate-y-0')
            setStickyState(false)
          } else if (!isIntersecting && target.childElementCount) {
            const widgetElement = target.firstElementChild as HTMLFormElement
            // If not visible in viewport, move the widget to the sticky wrapper
            stickyWrapper?.insertAdjacentElement(
              'afterbegin',
              widgetElement as HTMLFormElement,
            )

            setStickyState(true)

            // Update max-height for the sticky dropdown
            const stickyWrapperTop = stickyWrapper.getBoundingClientRect().top
            const maxHeight = `calc(100vh - ${86 - stickyWrapperTop}px)`
            widgetElement.style.setProperty(`--dropdown-max-height`, maxHeight)
          }
        },
      )

      intersectionObserver.observe(desktopContainerRef.current)

      return () => {
        intersectionObserver.disconnect()
      }
    }
  }, [
    desktopContainerRef,
    desktopWidgetRef,
    isDesktop,
    setStickyState,
    noSticky,
  ])

  useEffect(() => {
    if (!isDesktop) {
      setSticky(false)
    }
  }, [isDesktop])

  return typeof isDesktop === 'boolean' ? (
    <BookingWidgetProvider
      addresses={addresses}
      areAutoCompleteItemsLoading={areAutoCompleteItemsLoading}
      autoCompleteItems={autoCompleteItems}
      brand={brand}
      computeTotal={computeTotalPrice}
      loadingServiceLevels={loadingServiceLevels}
      serviceOptions={serviceOptions}
      handleData={onChange}
      onInputSearchTermHandlers={onInputSearchTermHandlers}
      onSelectAutoCompleteItemHandlers={onSelectAutoCompleteItemHandlers}
      partner={partner}
      partnerField={partnerField}
      productOptions={productOptions}
      totalPriceFormatted={totalPriceFormatted}
    >
      {isDesktop ? (
        <div
          ref={desktopContainerRef as MutableRefObject<HTMLDivElement>}
          className="h-[116px] w-full"
        >
          <BookingWidgetContent
            sticky={isSticky}
            ref={desktopWidgetRef as MutableRefObject<HTMLFormElement>}
            onSubmit={handleSubmit}
          />
        </div>
      ) : (
        <>
          <BookingWidgetMobile onSubmit={handleSubmit} />
        </>
      )}
    </BookingWidgetProvider>
  ) : null
}
